
import React from 'react';
import { Card } from "@/components/ui/card";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';
import { Lead } from '@/types/leads';

interface LeadsAnalyticsProps {
  leads: Lead[];
}

const LeadsAnalytics: React.FC<LeadsAnalyticsProps> = ({ leads }) => {
  const getSourceData = () => {
    const sourceCounts = leads.reduce((acc, lead) => {
      if (lead.source) {
        acc[lead.source] = (acc[lead.source] || 0) + 1;
      }
      return acc;
    }, {} as Record<string, number>);

    return Object.entries(sourceCounts).map(([name, value]) => ({
      name,
      value
    }));
  };

  const getQualityAndSpamData = () => {
    const qualityData = leads.reduce((acc, lead) => {
      if (lead.lead_quality) {
        acc.push({
          name: lead.lead_quality.replace('-', ' '),
          value: 1,
          type: 'Quality'
        });
      }
      if (lead.spam_rating) {
        acc.push({
          name: `Rating ${lead.spam_rating}`,
          value: 1,
          type: 'Spam Rating'
        });
      }
      return acc;
    }, [] as Array<{ name: string; value: number; type: string }>);

    return qualityData;
  };

  const getWonLeadsData = () => {
    const wonLeads = leads.filter(lead => lead.status === 'won');
    const totalWonLeads = wonLeads.length;
    const totalValue = wonLeads.reduce((sum, lead) => 
      sum + (parseFloat(lead.lead_value || '0') || 0), 0
    );
    
    return [
      {
        name: 'Won Leads',
        count: totalWonLeads,
        value: totalValue,
        displayValue: new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD'
        }).format(totalValue)
      }
    ];
  };

  const getCallsPerSalesPerson = () => {
    const salesPersonCalls = leads.reduce((acc, lead) => {
      if (lead.sales_person_assigned && lead.call_file) {
        acc[lead.sales_person_assigned] = (acc[lead.sales_person_assigned] || 0) + 1;
      }
      return acc;
    }, {} as Record<string, number>);

    return Object.entries(salesPersonCalls).map(([name, value]) => ({
      name,
      calls: value
    })).sort((a, b) => b.calls - a.calls);
  };

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042'];
  const QUALITY_COLORS = ['#8884d8', '#82ca9d'];

  return (
    <Card className="p-6">
      <h3 className="text-lg font-semibold mb-4">Lead Analytics</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        <div className="h-[300px]">
          <h4 className="text-sm font-medium mb-2">Leads by Source</h4>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={getSourceData()}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="value" fill="#8884d8" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="h-[300px]">
          <h4 className="text-sm font-medium mb-2">Lead Quality & Spam Rating Distribution</h4>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={getQualityAndSpamData()}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="value" fill="#8884d8" stackId="stack" name="Count" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="h-[300px]">
          <h4 className="text-sm font-medium mb-2">Won Leads Summary</h4>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={getWonLeadsData()}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis yAxisId="left" orientation="left" stroke="#8884d8" />
              <YAxis yAxisId="right" orientation="right" stroke="#82ca9d" />
              <Tooltip 
                formatter={(value, name) => {
                  if (name === 'value') {
                    return new Intl.NumberFormat('en-US', {
                      style: 'currency',
                      currency: 'USD'
                    }).format(value as number);
                  }
                  return value;
                }}
              />
              <Bar 
                yAxisId="left" 
                dataKey="count" 
                fill="#8884d8" 
                name="Number of Won Leads" 
              />
              <Bar 
                yAxisId="right" 
                dataKey="value" 
                fill="#82ca9d" 
                name={`Total Value (${getWonLeadsData()[0].displayValue})`}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="mt-8">
        <h4 className="text-sm font-medium mb-2">Calls per Sales Person</h4>
        <div className="h-[300px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={getCallsPerSalesPerson()}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="calls" fill="#82ca9d" name="Number of Calls" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </Card>
  );
};

export default LeadsAnalytics;

